/**
 * Cookie and tracking consent banner – compliant across regions (GDPR, CCPA, etc.).
 * When fullPage (mandatory gate): blocks app until user chooses Accept all or Essential only.
 * Full Privacy Policy and Terms are viewable on the banner; /privacy page remains for future review.
 * No tracking (including Google Analytics) until you accept.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useConsent } from '../contexts/ConsentContext';
import { PrivacyPolicyContent, TermsContent } from '../content/LegalContent';
import './ConsentBanner.css';

export default function ConsentBanner({ fullPage = false }) {
  const { showBanner, setConsent } = useConsent();
  const [expandedSection, setExpandedSection] = useState(null); // 'privacy' | 'terms' | null

  if (!fullPage && !showBanner) return null;

  const toggleSection = (section) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const inner = (
    <>
      <h2 id="consent-title" className="consent-banner-title">Cookie &amp; tracking consent</h2>
      <p id="consent-desc" className="consent-banner-desc">
        We use cookies to run the app (e.g. sign-in and preferences). With your consent we also use analytics
        (e.g. Google Analytics) to understand how the site is used and improve it. No analytics or non-essential
        tracking runs until you accept. You can change your choice at any time; we will ask again after 7 days if you
        decline.
      </p>
      {fullPage && (
        <p className="consent-banner-required">
          You must choose one option below to continue using the app.
        </p>
      )}

      {fullPage && (
        <div className="consent-banner-legal-toggle">
          <button
            type="button"
            className="consent-banner-toggle-btn"
            onClick={() => toggleSection('privacy')}
            aria-expanded={expandedSection === 'privacy'}
          >
            {expandedSection === 'privacy' ? '▼' : '▶'} View full Privacy Policy
          </button>
          {expandedSection === 'privacy' && (
            <div className="consent-banner-legal-scroll">
              <PrivacyPolicyContent className="consent-banner-legal-content" />
            </div>
          )}
          <button
            type="button"
            className="consent-banner-toggle-btn"
            onClick={() => toggleSection('terms')}
            aria-expanded={expandedSection === 'terms'}
          >
            {expandedSection === 'terms' ? '▼' : '▶'} View full Terms &amp; Conditions
          </button>
          {expandedSection === 'terms' && (
            <div className="consent-banner-legal-scroll">
              <TermsContent className="consent-banner-legal-content" />
            </div>
          )}
          <p className="consent-banner-full-page-link">
            <Link to="/privacy">Open full Privacy &amp; Terms page for future review</Link>
          </p>
        </div>
      )}

      <p className="consent-banner-terms">
        By choosing &quot;Accept all&quot; or &quot;Essential only&quot;, you agree to our{' '}
        <Link to="/privacy#terms">Terms &amp; Conditions</Link> and{' '}
        <Link to="/privacy#privacy-policy">Privacy Policy</Link>. If you are under 18 (or under 13 in the US),
        please ensure your parent or guardian has read our Privacy Policy and agrees to these terms where required
        by your region.
      </p>
      <div className="consent-banner-actions">
        <button
          type="button"
          className="consent-banner-btn consent-banner-btn-accept"
          onClick={() => setConsent(true)}
          aria-label="Accept all cookies and analytics"
        >
          Accept all
        </button>
        <button
          type="button"
          className="consent-banner-btn consent-banner-btn-essential"
          onClick={() => setConsent(false)}
          aria-label="Essential cookies only, no analytics"
        >
          Essential only
        </button>
        <Link to="/privacy" className="consent-banner-link" aria-label="Privacy policy and terms">
          Privacy policy &amp; terms
        </Link>
      </div>
    </>
  );

  if (fullPage) {
    return (
      <div
        className="consent-banner consent-banner--fullpage"
        role="dialog"
        aria-labelledby="consent-title"
        aria-describedby="consent-desc"
        aria-modal="true"
      >
        <div className="consent-banner-inner">{inner}</div>
      </div>
    );
  }

  return (
    <div className="consent-banner" role="dialog" aria-labelledby="consent-title" aria-describedby="consent-desc">
      <div className="consent-banner-inner">{inner}</div>
    </div>
  );
}
